import React, { useCallback, useMemo } from 'react'
import { uniqBy } from 'lodash'
import { useMatch } from 'react-router-dom'
import { WallboxLabelContext, WallboxLabelContextValue } from 'src/Libs/e-mobility/shared/providers/WallboxLabelContext'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFetchWallboxes } from 'src/Libs/e-mobility/wallboxes'
import { useChargingSessions } from 'src/Libs/e-mobility/charging-sessions/hooks/use-charging-sessions'
import { ChargingSessionModel } from 'src/Libs/e-mobility/charging-sessions'
import { getDuplicateWallboxNames } from 'src/Libs/e-mobility/charging-sessions/utils/get-duplicate-wallbox-names'
import { formatWallboxLabel } from 'src/Libs/e-mobility/charging-sessions/utils/format-wallbox-label'

interface WallboxLabelProviderProps {
  children: React.ReactNode
}

/**
 * Provides a helper map for generating the most appropriate wallbox label
 * to display to the user. The goal is to show as little identifying info
 * as possible, while maintaining clarity when duplicate names exist.
 *
 * Logic:
 * - If the wallbox has a **unique name**, show only the name.
 * - If the wallbox has **no name**, show only the ID.
 * - If the wallbox has a **name shared** with another wallbox (either
 *   active, historical, or soft-deleted from session history),
 *   show both name and ID to disambiguate.
 *
 * Internally:
 * - Fetches active wallboxes from `/wallboxes`.
 * - Fetches all charging sessions to collect historical wallboxes.
 * - Detects duplicate names across both sources.
 * - Returns `wallboxLabelMap`, which stores the wallbox label for each wallbox id
 */
export const WallboxLabelProvider: React.FC<WallboxLabelProviderProps> = ({ children }) => {
  const match = useMatch('/e-mobility/:installationId/*')
  const installationId = match?.params.installationId
  const { t } = useCustomTranslation()

  const { wallboxes: activeWallboxes, isLoading: isWallboxesLoading, isError: isWallboxesError } = useFetchWallboxes(Number(installationId))
  const {
    unfilteredData,
    isLoading: isSessionsLoading,
    isError: isSessionsError,
  } = useChargingSessions({ installationId: Number(installationId), midMeter: [] })

  const historicalWallboxes: Omit<ChargingSessionModel['wallbox'], 'type'>[] = useMemo(
    () =>
      (unfilteredData || []).map(session => ({
        id: session.wallbox.id,
        name: session.wallbox.name,
      })),
    [unfilteredData]
  )

  const allWallboxes: Omit<ChargingSessionModel['wallbox'], 'type'>[] = useMemo(
    () =>
      uniqBy(
        [
          ...(activeWallboxes || []).map(activeWallbox => ({
            id: activeWallbox.wallboxId,
            name: activeWallbox.wallboxName,
          })),
          ...historicalWallboxes,
        ],
        wallbox => wallbox.id
      ),
    [activeWallboxes, historicalWallboxes]
  )

  const duplicateWallboxNames = useMemo(() => getDuplicateWallboxNames(allWallboxes), [allWallboxes])

  const getWallboxLabel = useCallback(
    ({ wallboxId }: { wallboxId: string }) => {
      const wallboxName = allWallboxes.find(wallbox => wallbox.id === wallboxId)?.name

      if (isWallboxesLoading || isWallboxesError || isSessionsLoading || isSessionsError || !unfilteredData || !activeWallboxes || !wallboxName) {
        return formatWallboxLabel(
          {
            wallbox: {
              id: wallboxId,
              name: wallboxName,
            },
            showId: true,
          },
          t
        )
      }

      return formatWallboxLabel(
        {
          wallbox: {
            id: wallboxId,
            name: wallboxName,
          },
          showId: !!wallboxName && duplicateWallboxNames.includes(wallboxName),
        },
        t
      )
    },
    [
      activeWallboxes,
      isSessionsError,
      isSessionsLoading,
      unfilteredData,
      isWallboxesError,
      isWallboxesLoading,
      duplicateWallboxNames,
      allWallboxes,
      t,
    ]
  )

  const wallboxLabelMap = useMemo(() => {
    return Object.fromEntries(allWallboxes.map(wallbox => [wallbox.id, getWallboxLabel({ wallboxId: wallbox.id })]))
  }, [allWallboxes, getWallboxLabel])

  const contextValue = useMemo<WallboxLabelContextValue>(
    () => ({
      wallboxLabelMap,
    }),
    [wallboxLabelMap]
  )

  return <WallboxLabelContext.Provider value={contextValue}>{children}</WallboxLabelContext.Provider>
}
